import {maybeStr, requireNum} from "./utils";
import {GeoAvailability, numToGeoAvailability} from "@/store/modules/geo";
import {JLocation, RegReminderConfig, NativeApiResponse, NotificationManagerStatus, BackgroundLocationPermission} from "@/modules/models";

const promises = new Map<string, Promise<NativeApiResponse>>();

(window as any).resolveATPromise = function (promiseId: string, data: string[], error?: string) {
    const promise = promises[promiseId];
    if(!promise) { // Can happen if the page is reloaded while there are pending promises/deferred.
        console.info(`resolveATPromise: Promise with id ${promiseId} was not found.`);
    } else {
        if (error) {
            // Try parsing error as json, return json object if successful, return raw string if not.
            let err = new Error(error);
            if (err.message.length > 1) {
                try {
                    const obj = JSON.parse(error);
                    if (obj.message) { // serialized Error object
                        if (obj.code) { // Serialized NativeApiException
                            err = new NativeApiException(obj.message, obj.code);
                        } else {
                            err = obj as Error;
                        }
                    }
                } catch (e) {
                    // Do nothing, just return raw error string
                }
            }
            promise.reject(err);
        } else {
            const response = NativeApiResponse.fromJson(data[0]);
            promise.resolve(response);
        }
        delete promises[promiseId];
    }
};

export enum OS {
    iOS = "iOS",
    Android = "Android",
    Other = "Other",
}

class Version {
    readonly major: number;
    readonly minor: number;
    readonly patch: number;

    constructor(major: number, minor: number, patch: number) {
        this.major = major;
        this.minor = minor;
        this.patch = patch;
    }

    public toString() {
        return `${this.major}.${this.minor}.${this.patch}`;
    }
}

export interface NativeAppInfo {
    readonly version: Version;
    readonly os: OS;
}

interface AndroidInterface {
    connection(action: string, payload: string , promiseId: string): void;
}

interface IosMessage {
    action: string;
    payload: string;
    promiseId: string;
}

interface IosInterface {
    postMessage(msg: IosMessage);
}

// Matches enum defined in android NativeApi.kt
export enum NativeApiExceptionCode {
    ENETUNREACH = 10001,
    ETIMEDOUT = 10002,
    UNAVAILABLE = 10003,
    DEADLINE_EXCEEDED = 10004,
    MISSING_PERMISSION_PROMPTABLE = 10005,
    NATIVE_EXCEPTION = 10006,
    MOCKED_LOCATION = 10007,
    GEOGRANTED_INACCURATE_PROMPTABLE = 10008,
    LOCATION_SERVICE_UNAVAIL = 10009,
    GEOGRANTED_INACCURATE_NON_PROMPTABLE = 10010,
    MISSING_PERMISSION_NON_PROMPTABLE = 10011,
}

export class NativeApiException extends Error {
    private _code: number | null;

    constructor(message?: string, code?: number) {
        const msg = NativeApiException.msg(code) || message || `Ukjent feil`;
        super(msg);
        this._code = code || null;
    }

    get code() {
        return this._code;
    }

    static msg(code?: number) {
        switch (code) {
            case NativeApiExceptionCode.ENETUNREACH: return `Nettverk utilgjengelig`;
            case NativeApiExceptionCode.ETIMEDOUT: return `Nettverksforespørsel fikk ikke svar innen tidsgrense`;
            case NativeApiExceptionCode.UNAVAILABLE: return `Server utilgjengelig`;
            case NativeApiExceptionCode.DEADLINE_EXCEEDED: return `Forespørsel til server brukte for lang tid`;
            case NativeApiExceptionCode.MISSING_PERMISSION_PROMPTABLE: return `Tillatelse ikke gitt, kan forespørres`;
            case NativeApiExceptionCode.GEOGRANTED_INACCURATE_PROMPTABLE: return `Mangler nøyaktig posisjonstillatelse, kan forespørres`;
            case NativeApiExceptionCode.LOCATION_SERVICE_UNAVAIL: return `Posisjonstjeneste ikke tilgjengelig`;
            case NativeApiExceptionCode.GEOGRANTED_INACCURATE_NON_PROMPTABLE: return `Mangler nøyaktig posisjonstillatelse, kan ikke forespørres`;
            case NativeApiExceptionCode.MISSING_PERMISSION_NON_PROMPTABLE: return `Tillatelse ikke gitt, kan ikke forespørres`;
        }
        return null;
    }
}

export interface NativeApi {
    readonly available: boolean;
    readonly versionString: string;
    readonly os: OS;
    atleastVersion(major: number, minor: number, patch: number): boolean;
    // Check if geo permission is granted
    geoCheckAvailability(): Promise<GeoAvailability>;
    // Get next location update, different from the one passed in.
    // If null is passed in it should return current location if any exists.
    // If no location is found within timeout seconds, return null.
    geoNext(current: JLocation | null, timeoutSeconds: number, minDiffMeters: number): Promise<JLocation | null>;
    // Abort any ongoing geoNext calls. geoNext will then return immediatly with current location or null.
    geoStop(): Promise<void>;
    // Propagate mobile-service auth token and user session personRef from web-login to app.
    setRpcAuthtoken(token: string, personRef: string): Promise<void>;
    setServerConfiguration(selectedConfiguration: number): Promise<void>;
    getRegReminderConfig(): Promise<RegReminderConfig>;
    setRegReminderConfig(c: RegReminderConfig): Promise<RegReminderConfig>;
    fixLocationSettings(): Promise<boolean>;
    // Start qrcode reader activity and return read qrcode result (if any) when it closes.
    getQrcode(): Promise<string|null>;
    // Get the apps NotificationManagerStatus, I.E. if the app is currently allowed to display notifications to user.
    getNotificationManagerStatus(): Promise<NotificationManagerStatus>;
    // Open app notification settings, so user can enable notifications
    openNotificationSettings(): Promise<void>;
    requestReview(): Promise<boolean>;
    openAppSettings(): Promise<boolean>;
    checkBackgroundLocationPermission(): Promise<BackgroundLocationPermission>;
}

export class NativeBridge implements NativeApi {

    /**
     * Extracts info from user agent string to determine if request comes from native app, on either android or ios).
     * The Native apps must add the neccessary user agent string to the default one.
     * User agent string format: ".. Anleggsterminal/<version> (<os>, Mobile) .."
     * This way we can know that the user is running through our app, wether we're on android or ios and what version of
     * the app the user has installed.
     * @param ua The user agent string (window.navigator)
     * @return The resolved app info, or null if the current request was from a normal browser and not our app.
     */
    public static resolveAppInfo(ua: string): NativeAppInfo | null {
        const matched: RegExpMatchArray | null = ua.match(/.*\sAnleggsterminal\/(\d+)\.(\d+)\.(\d+)\s\((.+)\).*/);
        if(matched) {
            const version = new Version(requireNum(matched[1]), requireNum(matched[2]), requireNum(matched[3]));
            const commainfo = matched[4].split(",").map(str => str.trim());
            let os = OS.Other;
            for(let info of commainfo) {
                if(info === OS.iOS) {
                    os = OS.iOS;
                    break;
                } else if(info === OS.Android) {
                    os = OS.Android;
                    break;
                }
            }
            return {
                version: version,
                os: os,
            };
        }
        return null;
    }

    private appInfo: NativeAppInfo | null;
    private androidInterface: AndroidInterface | null = null;
    private iosInterface: IosInterface | null = null;

    constructor(window: Window) {
        this.appInfo = NativeBridge.resolveAppInfo(window.navigator.userAgent);
        if(this.appInfo) {
            if(this.appInfo.os === OS.Android) {
                this.androidInterface = window["androidJsInterface"] || null;
                if(!this.androidInterface) {
                    throw new Error("androidJsInterface not found on window. NativeBridge not available");
                }
            } else if(this.appInfo.os === OS.iOS) {
                let tmp = window["webkit"];
                tmp = tmp && tmp["messageHandlers"];
                tmp = tmp && tmp["connection"];
                if(tmp && tmp["postMessage"]) {
                    this.iosInterface = tmp as IosInterface;
                } else {
                    throw new Error("webkit.messageHandlers.connection.postMessage not found on window. NativeBridge not available");
                }
            }
        }
    }

    get available() {
        return this.appInfo != null && (this.androidInterface != null || this.iosInterface != null);
    }

    /**
     * To check that a certain native api function is available, use this to check against installed app version.
     * @param major
     * @param minor
     * @param patch
     */
    atleastVersion(major: number, minor: number, patch: number): boolean {
        const version = this.appInfo && this.appInfo.version;
        if(version) {
            return version.major > major ||
                (version.major == major && version.minor > minor) ||
                (version.major == major && version.minor == minor  && version.patch >= patch);
        }
        return false;
    }

    get versionString() {
         return this.appInfo && this.appInfo.version && this.appInfo.version.toString() || "";
    }

    get os(): OS {
        if(this.appInfo) {
            return this.appInfo.os;
        }
        return OS.Other;
    }

    toString(): string {
        return `NativeApi: ${this.versionString}`;
    }

    private async callNativeConnection(action: string, payload: string): Promise<NativeApiResponse> {
        if(this.available) {
            return new Promise((resolve, reject) => {
                // generate a unique id to reference the promise later from native function
                const promiseId = this.generateUUID();
                // save reference to promise in the global variable
                if(promises.has(promiseId)) {
                    throw new Error(`promiseId ${promiseId} exists already.`);
                }
                promises[promiseId] = {resolve, reject};
                if (this.androidInterface != null) {
                    this.androidInterface.connection(action, payload, promiseId);
                } else if (this.iosInterface != null) {
                    this.iosInterface.postMessage({
                        action: action,
                        payload: payload,
                        promiseId: promiseId
                    });
                }
            });
        } else {
            throw new Error("Native app bridge not available");
        }
    }

    private generateUUID(): string {
        let d = new Date().getTime();
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (d + Math.random() * 16) % 16 | 0;
            d = Math.floor(d / 16);
            return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });
    }

    /**
     * Checks if user has granted app permission to access fine geolocation so that
     */
    async geoCheckAvailability(): Promise<GeoAvailability> {
        const resp = await this.callNativeConnection("geoCheckAvailability", "");
        return numToGeoAvailability(requireNum(resp.payload));
    }

    /**
     * Get next location different from current. If current is null, return current location if available.
     * If location cannot be found, Promise should be rejected with error that has code property matching browser PositionError (https://developer.mozilla.org/en-US/docs/Web/API/PositionError).
     *
     * @param current the current location (last returned from this function). Call should then only return when a newer location is available.
     * @param timeoutSeconds When current is null (request for current location), wait max this many seconds before returning.
     */
    async geoNext(current: JLocation | null, timeoutSeconds: number, minDiffMeters: number): Promise<JLocation | null> {
        const payload = {current: current, timeoutSeconds: timeoutSeconds, minDiffMeters: minDiffMeters};
        const resp = await this.callNativeConnection("geoNext", JSON.stringify(payload));
        if (resp.payload) {
            return JLocation.fromJson(resp.payload);
        }
        return null;

    }

    /**
     * Abort any pending geoNext calls immediatly.
     * When this is called, the app should resolve any pending geoNext promises immediatly with null as value, and then stop geolocation listener in the app.
     */
    async geoStop(): Promise<void> {
        await this.callNativeConnection("geoStop", "");
    }

    async setRpcAuthtoken(token: string, personRef: string): Promise<void> {
        const payload = {token: token, personRef: personRef};
        await this.callNativeConnection("setRpcAuthtoken", JSON.stringify(payload));
    }

    async setServerConfiguration(selectedConfiguration: number): Promise<void> {
        await this.callNativeConnection("setServerConfiguration", JSON.stringify(selectedConfiguration));
    }

    async getRegReminderConfig(): Promise<RegReminderConfig> {
        const resp = await this.callNativeConnection("getRegReminderConfig", JSON.stringify({}));
        if(resp.payload) {
            return RegReminderConfig.fromJson(resp.payload);
        }
        throw new Error("Could not get RegReminderConfig");
    }

    async setRegReminderConfig(c: RegReminderConfig): Promise<RegReminderConfig> {
        const resp = await this.callNativeConnection("setRegReminderConfig", JSON.stringify(c));
        if(resp.payload) {
            return RegReminderConfig.fromJson(resp.payload);
        }
        throw new Error('RegReminderConfig not returned from save');
    }

    async fixLocationSettings(): Promise<boolean> {
        const resp = await this.callNativeConnection("fixLocationSettings", JSON.stringify({}));
        return resp.payload === true;
    }

    async getQrcode(): Promise<string|null> {
        const resp = await this.callNativeConnection("getQrcode", JSON.stringify({}));
        return maybeStr(resp.payload);
    }

    async getNotificationManagerStatus(): Promise<NotificationManagerStatus> {
        if(this.available) {
            const resp = await this.callNativeConnection("getNotificationManagerStatus", JSON.stringify({}));
            return NotificationManagerStatus.fromJson(resp.payload);
        } else {
            return new NotificationManagerStatus(true, new Map<string, boolean>()); // Default value
        }
    }

    async openNotificationSettings(): Promise<void> {
        if(this.available) {
            await this.callNativeConnection("openNotificationSettings", JSON.stringify({}));
        }
        return;
    }

    /**
     * Request a start of the user app review flow in android/ios app. The phone OS has its own rate limit for this, so this call might be disregarded by the phone, and have no effect.
     * @return true if review (probably) was requested successfully, false if something failed.
     */
    async requestReview(): Promise<boolean> {
        try {
            if(this.available && this.atleastVersion(1, 2, 0)) {
                await this.callNativeConnection("requestReview", JSON.stringify({}));
                return true;
            }
        } catch (e) {
            console.warn(`Requesting in app user review failed: ${e}`);
        }
        return false;
    }

    async openAppSettings(): Promise<boolean> {
        if(this.atleastVersion(1, 3, 0)) {
            const resp = await this.callNativeConnection("openAppSettings", JSON.stringify({}));
            return resp.payload === true;
        } else {
            return await this.fixLocationSettings(); // Best effort for when app is outdated
        }
    }

    async checkBackgroundLocationPermission(): Promise<BackgroundLocationPermission> {
        if(this.atleastVersion(1, 3, 0)) {
            const resp = await this.callNativeConnection("checkBackgroundLocationPermission", JSON.stringify({}));
            return BackgroundLocationPermission.fromJson(resp.payload);
        } else {
            // Should never happen, we should check the api version before calling this method.
            throw new Error(`checkBackgroundLocationPermission not implemented`);
        }
    }
}